import type { ReactNode } from 'react';
import { cn } from '../cn.js';

export interface SettingRowProps {
  icon?: ReactNode;
  title: string;
  subtitle?: string;
  /** Right-hand control or value (switch, chevron, text). */
  trailing?: ReactNode;
  onClick?: () => void;
  className?: string;
}

/** A single row inside a settings / list group. */
export function SettingRow({
  icon,
  title,
  subtitle,
  trailing,
  onClick,
  className,
}: SettingRowProps): JSX.Element {
  const interactive = Boolean(onClick);
  return (
    <div
      className={cn('as-row', interactive && 'as-row--interactive', className)}
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
    >
      {icon && <span className="as-row__icon">{icon}</span>}
      <span className="as-row__text">
        <span className="as-row__title">{title}</span>
        {subtitle && <span className="as-row__subtitle">{subtitle}</span>}
      </span>
      {trailing && <span className="as-row__trailing">{trailing}</span>}
    </div>
  );
}

export interface SettingGroupProps {
  title?: string;
  children: ReactNode;
  className?: string;
}

/** A titled group of setting rows rendered as one card. */
export function SettingGroup({ title, children, className }: SettingGroupProps): JSX.Element {
  return (
    <section className={cn('as-group', className)}>
      {title && <h3 className="as-group__title">{title}</h3>}
      <div className="as-group__card">{children}</div>
    </section>
  );
}
